import React, { useState, useRef, useEffect } from "react";
import { SocialMediaLink } from "../types";
import { MoveLeft, MoveRight, Plus, X } from "lucide-react";
import Drawer from "./ui/Drawer";
import SocialMediaForm from "./SocialMediaForm";

declare global {
  interface Window {
    instgrm?: {
      Embeds: {
        process: () => void;
      };
    };
  }
}
interface SocialMediaPreviewProps {
  links: SocialMediaLink[];
  isEditing?: boolean;
  isEditingPunishment?: boolean;
  isEditingDraftIdeas?: boolean;
  contentTitle?: string;
  contentId?: string;
  onDelete?: (id: string) => void;
  contentType?: string | "punishment";
}
type PlatformStatus = "loading" | "success" | "error";

const SocialMediaPreview: React.FC<SocialMediaPreviewProps> = ({
  links,
  isEditing = false,
  isEditingPunishment = false,
  isEditingDraftIdeas = false,
  onDelete,
  contentTitle,
  contentId,
  contentType = "punishment",
}) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const loadedPlatforms = useRef<Set<string>>(new Set());

  // Track loading state of scripts to avoid duplicate loads
  const scriptLoadingRef = useRef<{ [key: string]: boolean }>({});

  // Initialize platform status for all platforms
  const [platformStatus, setPlatformStatus] = useState<{
    [key: string]: PlatformStatus;
  }>(() => {
    const initial: { [key: string]: PlatformStatus } = {};
    links.forEach((link) => {
      if (link.type === "twitter" || link.type === "x") {
        initial["twitter"] = "loading";
      } else {
        initial[link.type] = "loading";
      }
    });
    return initial;
  });

  // Update the loadPlatformScript function
  const loadPlatformScript = (platform: string, url: string): Promise<void> => {
    if (scriptLoadingRef.current[platform]) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      if (loadedPlatforms.current.has(platform)) {
        resolve();
        return;
      }

      scriptLoadingRef.current[platform] = true;
      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.id = `${platform}-script`;

      const cleanup = () => {
        scriptLoadingRef.current[platform] = false;
      };

      script.onload = () => {
        loadedPlatforms.current.add(platform);
        cleanup();
        setPlatformStatus((prev) => ({ ...prev, [platform]: "success" }));
        resolve();
      };

      script.onerror = () => {
        cleanup();
        setPlatformStatus((prev) => ({ ...prev, [platform]: "error" }));
        reject(new Error(`Failed to load ${platform} script`));
      };

      setPlatformStatus((prev) => ({ ...prev, [platform]: "loading" }));
      document.body.appendChild(script);
    });
  };

  useEffect(() => {
    const isLoadingRef = { current: false };

    const loadScripts = async () => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;

      try {
        // Load all platform scripts first
        const uniquePlatforms = new Set(
          links.map((link) => (link.type === "x" ? "twitter" : link.type))
        );

        // Load scripts in parallel but handle failures independently
        await Promise.allSettled(
          Array.from(uniquePlatforms).map(async (platform) => {
            try {
              switch (platform) {
                case "instagram":
                  await loadPlatformScript(
                    "instagram",
                    "//www.instagram.com/embed.js"
                  );
                  break;
                case "twitter":
                  await loadPlatformScript(
                    "twitter",
                    "https://platform.twitter.com/widgets.js"
                  );
                  break;
                case "tiktok":
                  await loadPlatformScript(
                    "tiktok",
                    "https://www.tiktok.com/embed.js"
                  );
                  break;
              }
            } catch (error) {
              console.error(`Error loading ${platform} script:`, error);
              setPlatformStatus((prev) => ({ ...prev, [platform]: "error" }));
            }
          })
        );

        // Process embeds sequentially to avoid race conditions
        for (const link of links) {
          const platform = link.type === "x" ? "twitter" : link.type;
          try {
            switch (platform) {
              case "instagram":
                if (window.instgrm?.Embeds?.process) {
                  window.instgrm.Embeds.process();
                }
                break;
              case "twitter":
                if (!window.twttr?.widgets) continue;
                const urlObj = new URL(link.url);
                const tweetId = urlObj.pathname.split("/").pop();
                if (!tweetId) continue;

                const index = links.indexOf(link);
                const container = document.getElementById(
                  `tweet-container-${index}`
                );
                if (container && container.children.length === 0) {
                  await window.twttr.widgets.createTweet(tweetId, container, {
                    conversation: "none",
                    align: "center",
                    width: 350,
                  });
                }
                break;
              case "tiktok":
                // TikTok processes automatically
                break;
            }
            setPlatformStatus((prev) => ({ ...prev, [platform]: "success" }));
          } catch (error) {
            console.error(`Error processing ${platform} embed:`, error);
            setPlatformStatus((prev) => ({ ...prev, [platform]: "error" }));
          }
        }
      } finally {
        isLoadingRef.current = false;
      }
    };

    loadScripts();

    return () => {
      // Cleanup scripts and containers
      Array.from(loadedPlatforms.current).forEach((platform) => {
        const script = document.getElementById(`${platform}-script`);
        if (script) {
          document.body.removeChild(script);
        }
      });
      links.forEach((_, index) => {
        const container = document.getElementById(`tweet-container-${index}`);
        if (container) {
          container.innerHTML = "";
        }
      });
      loadedPlatforms.current.clear();
    };
  }, [links]);

  // Update the render logic for each platform type
  const renderLoadingState = (platform: string) => (
    <div className="flex flex-col items-center justify-center h-[200px] bg-gray-50 rounded-lg">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
      <p className="text-gray-500 text-sm">Loading {platform}...</p>
    </div>
  );

  const renderErrorState = (platform: string) => (
    <div className="flex items-center justify-center h-[200px] bg-red-50 rounded-lg">
      <p className="text-red-500 text-sm">Failed to load {platform}</p>
    </div>
  );

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (carouselRef.current?.offsetLeft || 0));
    setScrollLeft(carouselRef.current?.scrollLeft || 0);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    if (!carouselRef.current) return;

    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleScroll = (direction: "left" | "right") => {
    if (!carouselRef.current) return;
    const scrollAmount = carouselRef.current.offsetWidth;
    const newScrollLeft =
      direction === "left"
        ? carouselRef.current.scrollLeft - scrollAmount
        : carouselRef.current.scrollLeft + scrollAmount;

    carouselRef.current.scrollTo({
      left: newScrollLeft,
      behavior: "smooth",
    });
  };

  const [drawerOpen, setDrawerOpen] = useState(false);
  const handleOpenDrawer = () => {
    console.log("Opening drawer for contentId:", contentId);
    if (contentId) setDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
  };

  const getEmbedComponent = (link: SocialMediaLink) => {
    try {
      const url = new URL(link.url);

      switch (link.type) {
        case "instagram": {
          return (
            <>
              <div className="w-full flex items-center justify-center">
                <div className="w-full max-w-[328px] sm:max-w-[540px] flex items-center">
                  <blockquote
                    className="instagram-media !m-0 !min-w-0 w-full"
                    data-instgrm-permalink={link.url}
                    data-instgrm-version="14"
                    data-instgrm-captioned
                    style={{
                      margin: "0 !important",
                      width: "100% !important",
                      minWidth: "0 !important",
                      border: "none !important",
                    }}
                  />
                </div>
              </div>
              <div className="submitPunishmentDrawer"></div>
            </>
          );
        }

        case "youtube": {
          const videoId =
            url.searchParams.get("v") || url.pathname.split("/").pop();
          return (
            <div className="relative w-full pt-[56.25%]">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${videoId}`}
                allowFullScreen
                loading="lazy"
                title={`YouTube video ${videoId}`}
              />
            </div>
          );
        }
        case "tiktok": {
          const videoId = url.pathname.split("/").pop();
          return (
            <blockquote
              className="tiktok-embed !m-0"
              cite={link.url}
              data-video-id={videoId}
              style={{
                maxWidth: "100% !important",
                minWidth: "unset !important",
              }}
            >
              <section></section>
            </blockquote>
          );
        }
        default:
          return null;
      }
    } catch (error) {
      console.error("Error parsing social media URL:", error);
      return null;
    }
  };

  if (!links || links.length === 0) return null;

  return (
    <>
      <div className="w-full px-2 sm:px-4">
        <div className="max-w-[2000px] mx-auto">
          <div
            ref={carouselRef}
            className={`flex overflow-x-auto hide-scrollbar gap-2 sm:gap-4 mb-4 sm:mb-6 pb-8 ${
              links.length > 3 ? "justify-left" : "justify-center"
            }`}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            style={{
              cursor: isDragging ? "grabbing" : "grab",
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {links.map((link, index) => (
              <div
                key={link.id || `link-${index}`}
                className="flex-shrink-0 w-[328px] sm:w-[350px] lg:w-[400px]"
                style={{ scrollSnapAlign: "start" }}
              >
                <div className="p-2 sm:p-4">
                  <div
                    className={`
                  relative bg-white rounded-[20px] sm:rounded-[29px] 
                  overflow-hidden shadow-[0_15px_20px_rgba(0,0,0,0.15)] transition-all duration-300 
                  hover:shadow-xl hover:scale-[1.02]
                `}
                  >
                    {(isEditing ||
                      isEditingPunishment ||
                      isEditingDraftIdeas) &&
                      onDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(link.id);
                          }}
                          className="absolute top-2 right-2 sm:top-4 sm:right-4 z-10 p-1 
                             bg-red-500 rounded-full text-white hover:bg-red-600 
                             transition-colors"
                          aria-label="Delete"
                        >
                          <X size={16} className="sm:w-5 sm:h-5" />
                        </button>
                      )}

                    <div className="relative w-full">
                      {/* Container for Instagram */}
                      {link.type === "instagram" && (
                        <div className="w-full max-h-[600px] sm:max-h-[700px]">
                          <div className="w-full h-full flex items-center justify-center p-2 sm:p-4">
                            <div className="w-full max-w-[328px] sm:max-w-[540px] overflow-hidden">
                              <blockquote
                                className="instagram-media !m-0 !min-w-0 w-full"
                                data-instgrm-permalink={link.url}
                                data-instgrm-version="14"
                                data-instgrm-captioned
                                style={{
                                  margin: "0 !important",
                                  width: "100% !important",
                                  minWidth: "0 !important",
                                  border: "none !important",
                                  maxHeight: "600px",
                                  overflowY: "auto",
                                  overflowX: "hidden",
                                  WebkitOverflowScrolling: "touch",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Container for Custom */}
                      {link.type === "custom" && (
                        <div className="addSlideBlk">
                          <h2 className="sliderHeading">Submit your version</h2>
                          <div className="addSlide" onClick={handleOpenDrawer}>
                            <Plus strokeWidth="1.3" size={32} />
                            <p>add</p>
                          </div>
                          <p className="shareTxt">Share you punishment</p>
                        </div>
                      )}

                      {/* Container for Twitter */}
                      {(link.type === "twitter" || link.type === "x") && (
                        <div className="w-full min-h-[500px] sm:min-h-[600px] flex items-center justify-center">
                          <div className="w-full max-w-[350px] sm:max-w-[550px]">
                            <div id={`tweet-container-${index}`}></div>
                          </div>
                        </div>
                      )}

                      {/* Container for YouTube */}
                      {link.type === "youtube" && (
                        <div className="w-full aspect-video">
                          <iframe
                            className="w-full h-full"
                            src={`https://www.youtube.com/embed/${
                              new URL(link.url).searchParams.get("v") ||
                              new URL(link.url).pathname.split("/").pop()
                            }`}
                            allowFullScreen
                            loading="lazy"
                            title={`YouTube video ${link.id}`}
                          />
                        </div>
                      )}

                      {/* Container for TikTok */}
                      {link.type === "tiktok" && (
                        <div className="w-full max-h-[600px] sm:max-h-[700px]">
                          <div className="w-full h-full flex items-center justify-center p-2 sm:p-4">
                            <div className="w-full max-w-[328px] sm:max-w-[540px] overflow-hidden">
                              <blockquote
                                className="tiktok-embed !m-0 !min-w-0 w-full"
                                cite={link.url}
                                data-video-id={new URL(link.url).pathname
                                  .split("/")
                                  .pop()}
                                style={{
                                  margin: "0 !important",
                                  width: "100% !important",
                                  minWidth: "0 !important",
                                  border: "none !important",
                                  maxHeight: "600px",
                                  overflowY: "auto",
                                  overflowX: "hidden",
                                  WebkitOverflowScrolling: "touch",
                                }}
                              >
                                <section></section>
                              </blockquote>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {links.length > 1 && (
            <div className="flex justify-center gap-2 sm:gap-4 mt-2 sm:mt-4">
              <button
                onClick={() => handleScroll("left")}
                className="hover:scale-105 transition-transform duration-200"
                aria-label="Previous"
              >
                <MoveLeft
                  strokeWidth="1.3"
                  className="w-12 h-12 sm:w-12 sm:h-12 border-solid border rounded-[50%] border-[#1A1A1A] p-2"
                  color="#1A1A1A"
                />
              </button>
              <button
                onClick={() => handleScroll("right")}
                className="hover:scale-105 transition-transform duration-200"
                aria-label="Next"
              >
                <MoveRight
                  strokeWidth="1.3"
                  className="w-12 h-12 sm:w-12 sm:h-12 border-solid border rounded-[50%] border-[#1A1A1A] p-2"
                  color="#1A1A1A"
                />
              </button>
            </div>
          )}
        </div>

        <style>{`
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  @media (max-width: 640px) {
    .instagram-media,
    .twitter-tweet,
    .tiktok-embed {
      max-width: 100% !important;
      min-width: 0 !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .instagram-media iframe,
    .twitter-tweet iframe,
    .tiktok-embed iframe {
      min-width: 0 !important;
    }
  }
`}</style>
      </div>
      {drawerOpen === true ? (
        <Drawer isOpen={drawerOpen} onClose={handleCloseDrawer}>
          <SocialMediaForm
            contentTitle={contentTitle}
            contentId={contentId}
            contentType={contentType}
          />
        </Drawer>
      ) : null}
    </>
  );
};

export default SocialMediaPreview;
